import { defineComponent, type FunctionalComponent, onBeforeMount, onMounted, type PropType, type VNode, watch } from "vue";
import { useRouter } from "vue-router";
import { enrichRelativeUrl } from "@utils/vue-migration/common/urlUtils";
import type { GaSpaContext } from "@/spa/App";
import isEqual from "lodash/isEqual";

export { defineComponent, type PropType, type Component, inject } from "vue";
export { useRoute, type LocationQuery } from "vue-router";
export type GaVueComponent = VNode;

export function createParentComponent<Props = Record<string, never>>(
  component: (props: Props & { children?: GaVueComponent | GaVueComponent[] }) => GaVueComponent,
): FunctionalComponent<Props, Record<string, never>, { default?: void }> /*(props: Props, ctx: GaVueContext) => GaVueComponent*/ {
  return (props, ctx) => component({ ...props, children: ctx.slots.default?.() });
}

/**
 * Runs `sideEffect` on `onBeforeMount`.
 */
export const GaLowLevelEffect = defineComponent({
  props: {
    sideEffect: {
      type: Function as PropType<() => Promise<void> | void>,
      required: true,
    },
    content: {
      type: Object as PropType<GaVueComponent>,
      required: true,
    },
  },
  setup(props) {
    onBeforeMount(props.sideEffect);
    return () => props.content;
  },
});

/**
 * Like `GaLowLevelEffect`, but runs `sideEffect` on `onMounted` (with DOM ready)
 */
export const GaLowLevelEffectOnMounted = defineComponent({
  props: {
    sideEffect: {
      type: Function as PropType<() => Promise<void> | void>,
      required: true,
    },
    content: {
      type: Object as PropType<GaVueComponent>,
      required: true,
    },
  },
  setup(props) {
    onMounted(props.sideEffect);
    return () => props.content;
  },
});

export const GaLowLevelEffectWithWatch = defineComponent({
  props: {
    sideEffect: {
      type: Function as PropType<() => Promise<void> | void>,
      required: true,
    },
    getWatchSource: {
      type: Function,
      required: true,
    },
    content: {
      type: Object as PropType<GaVueComponent>,
      required: true,
    },
  },
  setup(props) {
    onBeforeMount(props.sideEffect);
    watch(
      /* A delegated function is needed here. If `props.getWatchSource` gets passed directly, the watch function does not work as expected. Not sure why this is. */ () => props.getWatchSource(),
      async (after, before) => {
        if (isEqual(before, after)) {
          return;
        }
        await props.sideEffect();
      },
    );
    return () => props.content;
  },
});

export type MaybeRedirect<T> = { kind: "REDIRECT"; url: string } | { kind: "NO_REDIRECT"; data: T };

export function HandleRedirect<T>(props: { maybeRedirect: MaybeRedirect<T>; render: (noRedirect: T) => GaVueComponent; ctx: GaSpaContext }): GaVueComponent {
  const router = useRouter();
  if (props.maybeRedirect.kind === "REDIRECT") {
    const url = props.maybeRedirect.url;
    return (
      <GaLowLevelEffect
        sideEffect={async () => {
          await router.push(enrichRelativeUrl(url, props.ctx));
        }}
        content={<></>}
      />
    );
  } else {
    return props.render(props.maybeRedirect.data);
  }
}

export function HandleNullable<T>(props: { maybeNull: T | null; renderNull: () => GaVueComponent; renderData: (data: T) => GaVueComponent }): GaVueComponent {
  if (props.maybeNull === null) {
    return props.renderNull();
  } else {
    return props.renderData(props.maybeNull);
  }
}
